import React, { FC, useContext, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

import ImagePreviewThumbnail from '../../../sharedModules/imagePreview/ImagePreviewThumbnail';
import ImageGalleryModal from './ImageGalleryModal';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowFormImageSectionProps {
  backflowImageAlbum: any;
  fetchBackflowImageAlbum: () => void;
  category: string;
  label?: string;
  isOnline?: boolean;
  required?: boolean;
  error?: boolean;
}

const BackflowFormImageSection: FC<BackflowFormImageSectionProps> = (props) => {
  let { backflowImageAlbum, category, label, fetchBackflowImageAlbum, isOnline = true, required, error } = props;

  let { setSnackBarMessage, handleSnackbarAlertSeverityChange, setSnackBarOpen } = useContext(SnackBarContext);

  const [galleryModalOpen, setGalleryModalOpen] = useState<boolean>(false);

  // Hide component when offline
  if (!isOnline) {
    return (
      <p style={{ backgroundColor: 'lightgray', padding: 6, borderRadius: 5, marginBottom: 10 }}>
        Image Upload is disabled when offline.
      </p>
    );
  }

  const handleFileChange = async (files: FileList | null) => {
    if (files && files.length > 0) {
      try {
        const uploadFormData = new FormData();
        Array.from(files).forEach((file) => {
          uploadFormData.append('files[]', file);
        });
        uploadFormData.append('category', category);

        await axios.put(`/backflows/backflow_log_images/${backflowImageAlbum.id}`, uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        fetchBackflowImageAlbum();
        setSnackBarMessage(`${files.length} file(s) uploaded successfully!`);
        handleSnackbarAlertSeverityChange('success');
        setSnackBarOpen(true);
      } catch (error) {
        setSnackBarMessage('Failed to upload files. Please try again.');
        handleSnackbarAlertSeverityChange('error');
        setSnackBarOpen(true);
      }
    }
  };

  const images = backflowImageAlbum?.[`${category}`] || [];
  const hasMultipleImages = images.length >= 3;
  const displayImages = hasMultipleImages ? images.slice(0, 2) : images;

  return (
    <LoadingWrapper isLoading={!backflowImageAlbum?.id}>
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <PhotoLibraryIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {label}
              {required && (
                <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                  *
                </Box>
              )}
            </Typography>
            {images.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {images.length} {images.length === 1 ? 'item' : 'items'}
              </Typography>
            )}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
            <Button variant="contained" component="label" size="medium">
              Take Photo
              <input
                hidden
                id="upload-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileChange((e.target as HTMLInputElement).files)}
              />
            </Button>

            <Button variant="contained" color="success" component="label" size="medium">
              Take Video
              <input
                hidden
                id="upload-camera"
                type="file"
                accept="video/*"
                capture="environment"
                onChange={(e) => handleFileChange((e.target as HTMLInputElement).files)}
              />
            </Button>

            <Button variant="contained" color="secondary" component="label" size="medium">
              Gallery
              <input
                hidden
                id="upload-gallery"
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange((e.target as HTMLInputElement).files)}
              />
            </Button>
          </Stack>

          {images.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '2px dashed',
                borderColor: error ? 'error.main' : 'grey.300',
              }}
            >
              <PhotoLibraryIcon sx={{ fontSize: 48, color: error ? 'error.main' : 'grey.400', mb: 1 }} />
              <Typography variant="body2" color={error ? 'error' : 'text.secondary'}>
                No images uploaded yet
              </Typography>
              {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  This field is required
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {displayImages.map((image, index) => (
                  <Grid item xs={12} sm={6} md={hasMultipleImages ? 6 : 4} key={`image-${index}`}>
                    <ImagePreviewThumbnail image={image} altText={`${label} Image ${index + 1}`} />
                  </Grid>
                ))}
              </Grid>

              {hasMultipleImages && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setGalleryModalOpen(true)}
                    startIcon={<PhotoLibraryIcon />}
                    size="large"
                  >
                    View All {images.length} Images
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ImageGalleryModal
        open={galleryModalOpen}
        onClose={() => setGalleryModalOpen(false)}
        images={images}
        label={label || 'Images'}
      />
    </LoadingWrapper>
  );
};

export default BackflowFormImageSection;
